import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { secretsApi } from "@/lib/api/secrets";
import { projectsApi, Project } from "@/lib/api/projects";
import {
  SecretScope,
  SecretType,
  SecretMode,
  SecretResponse,
  EnvBudgetInfo,
} from "@optidev/shared-types";
import toast from "react-hot-toast";
import {
  Plus,
  Upload,
  Link,
  Search,
  HelpCircle,
  AlertCircle,
  FolderOpen,
  Split,
  Merge,
  Filter,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import AddSecretModal from "./AddSecretModal";
import EditSecretModal from "./EditSecretModal";
import ImportEnvModal from "./ImportEnvModal";
import ImportFromAccountModal from "./ImportFromAccountModal";
import SecretsTable from "./SecretsTable";
import ProjectSelector from "./ProjectSelector";

interface SecretsManagerProps {
  scope?: SecretScope; // Default scope ('account' | 'project')
  projectId?: string; // Pre-selected project ID
  showScopeTabs?: boolean; // Show Account/Project tabs (default: true)
  lockToProject?: boolean; // Hide project selector, disable scope switching (default: false)
  compact?: boolean; // Use compact header (default: false)
  hideHeader?: boolean; // Hide the header completely (default: false)
  className?: string; // Additional styling
  onSecretsChange?: () => void; // Callback when secrets are modified
}

export default function SecretsManager({
  scope: initialScope,
  projectId: initialProjectId,
  showScopeTabs = true,
  lockToProject = false,
  compact = false,
  hideHeader = false,
  className = "",
  onSecretsChange,
}: SecretsManagerProps) {
  const { getIdToken } = useAuth();

  // State
  const [activeScope, setActiveScope] = useState<SecretScope>(
    initialScope ||
      (initialProjectId ? SecretScope.PROJECT : SecretScope.ACCOUNT)
  );
  const [activeMode, setActiveMode] = useState<"lite" | "power">("lite");
  const [secrets, setSecrets] = useState<SecretResponse[]>([]);
  const [budget, setBudget] = useState<EnvBudgetInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState<SecretType | "">("");

  // Project selection
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
    initialProjectId || null
  );
  const [loadingProjects, setLoadingProjects] = useState(false);

  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showImportEnvModal, setShowImportEnvModal] = useState(false);
  const [showImportFromAccountModal, setShowImportFromAccountModal] =
    useState(false);
  const [editingSecret, setEditingSecret] = useState<SecretResponse | null>(
    null
  );

  // Load projects when switching to PROJECT scope
  useEffect(() => {
    if (activeScope === SecretScope.PROJECT && !lockToProject) {
      loadProjects();
    } else if (activeScope === SecretScope.ACCOUNT) {
      // Clear selection when switching to ACCOUNT
      if (!lockToProject) {
        setSelectedProjectId(null);
      }
    }
  }, [activeScope, lockToProject]);

  // Load secrets on mount and when filters change
  useEffect(() => {
    // Only load secrets if we're in ACCOUNT scope OR have a selected project
    if (activeScope === SecretScope.ACCOUNT || selectedProjectId) {
      loadSecrets();
    }
  }, [activeScope, selectedProjectId, searchQuery, typeFilter]);

  const loadProjects = async () => {
    try {
      setLoadingProjects(true);
      const token = await getIdToken();
      if (!token) return;

      const projectsList = await projectsApi.getAll(token);
      setProjects(projectsList);

      // If there's an initial projectId, make sure it's selected
      if (initialProjectId && !selectedProjectId) {
        setSelectedProjectId(initialProjectId);
      }
    } catch (error: any) {
      console.error("Failed to load projects:", error);
      toast.error(error.response?.data?.message || "Failed to load projects");
    } finally {
      setLoadingProjects(false);
    }
  };

  const loadSecrets = async () => {
    try {
      setLoading(true);
      const token = await getIdToken();
      if (!token) return;

      const response = await secretsApi.getAll(
        token,
        activeScope,
        activeScope === SecretScope.PROJECT
          ? selectedProjectId || undefined
          : undefined,
        searchQuery || undefined,
        typeFilter || undefined
      );

      setSecrets(response.secrets);
      setBudget(response.budget || null);
    } catch (error: any) {
      console.error("Failed to load secrets:", error);
      toast.error(error.response?.data?.message || "Failed to load secrets");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    setShowAddModal(false);
    await loadSecrets();
    toast.success("Secret created successfully");
    onSecretsChange?.(); // Notify parent of changes
  };

  const handleUpdate = async () => {
    setShowEditModal(false);
    setEditingSecret(null);
    await loadSecrets();
    toast.success("Secret updated successfully");
    onSecretsChange?.(); // Notify parent of changes
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this secret?")) return;

    try {
      const token = await getIdToken();
      if (!token) return;

      await secretsApi.delete(token, id);
      await loadSecrets();
      toast.success("Secret deleted successfully");
      onSecretsChange?.(); // Notify parent of changes
    } catch (error: any) {
      console.error("Failed to delete secret:", error);
      toast.error(error.response?.data?.message || "Failed to delete secret");
    }
  };

  const handleEdit = (secret: SecretResponse) => {
    setEditingSecret(secret);
    setShowEditModal(true);
  };

  const handleUnlink = async (secretId: string) => {
    if (!selectedProjectId) return;

    if (
      !confirm(
        'Are you sure you want to unlink this secret? You can re-link it anytime from "Link from Account".'
      )
    ) {
      return;
    }

    try {
      const token = await getIdToken();
      if (!token) return;

      await secretsApi.unlink(token, {
        secretId,
        projectId: selectedProjectId,
      });

      await loadSecrets();
      toast.success("Secret unlinked successfully");
      onSecretsChange?.(); // Notify parent of changes
    } catch (error: any) {
      console.error("Failed to unlink secret:", error);
      toast.error(error.response?.data?.message || "Failed to unlink secret");
    }
  };

  const handleToggleGlobalMode = async () => {
    // Toggle all secrets to the new mode
    const newMode = activeMode === "lite" ? "power" : "lite";

    try {
      const token = await getIdToken();
      if (!token) return;

      // Toggle mode for all secrets
      for (const secret of secrets) {
        await secretsApi.toggleMode(
          token,
          secret.id,
          newMode === "lite" ? SecretMode.LITE : SecretMode.POWER
        );
      }

      setActiveMode(newMode);
      await loadSecrets();
      toast.success(
        `Switched to ${newMode === "lite" ? "Lite" : "Power"} mode`
      );
    } catch (error: any) {
      console.error("Failed to toggle mode:", error);
      toast.error(error.response?.data?.message || "Failed to toggle mode");
    }
  };

  const handleImportEnv = async () => {
    setShowImportEnvModal(false);
    await loadSecrets();
    onSecretsChange?.(); // Notify parent of changes
  };

  const handleImportFromAccount = async () => {
    setShowImportFromAccountModal(false);
    await loadSecrets();
    onSecretsChange?.(); // Notify parent of changes
  };

  const handleScopeChange = (newScope: SecretScope) => {
    if (lockToProject) return; // Prevent scope change when locked
    setActiveScope(newScope);
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      {!hideHeader && (
        <>
          {compact ? (
            <div className="mb-10">
              <h1 className="text-3xl sm:text-4xl font-bold mb-3 bg-gradient-to-r from-purple-600 to-blue-600 dark:from-purple-400 dark:to-blue-500 bg-clip-text text-transparent">
                Secrets Manager
              </h1>
              <p className="text-muted-foreground text-base sm:text-lg">
                {activeMode === "lite"
                  ? "Manage environment variables with single values for all environments"
                  : "Manage environment variables with separate values for development, staging, and production"}
              </p>
            </div>
          ) : (
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">
                  Secrets Manager
                </h1>
                <p className="text-muted-foreground text-sm">
                  {activeMode === "lite"
                    ? "Lite mode: One value for all environments"
                    : "Power mode: Dev → Staging → Production • Same color = same value"}
                </p>
              </div>

              {/* Scope Tabs */}
              {showScopeTabs && !lockToProject && (
                <Tabs
                  value={activeScope}
                  onValueChange={(value) =>
                    handleScopeChange(value as SecretScope)
                  }
                >
                  <TabsList className="bg-muted">
                    <TabsTrigger value={SecretScope.ACCOUNT}>
                      Account
                    </TabsTrigger>
                    <TabsTrigger value={SecretScope.PROJECT}>
                      Project
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              )}
            </div>
          )}
        </>
      )}

      {/* Project Selector Row (only visible in Project scope and not locked) */}
      {!lockToProject && activeScope === SecretScope.PROJECT && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <FolderOpen className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              <Label className="font-semibold">Select Project:</Label>
              <ProjectSelector
                projects={projects}
                selectedId={selectedProjectId}
                onSelect={setSelectedProjectId}
                loading={loadingProjects}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Budget Warning */}
      {budget && budget.isNearLimit && (
        <Card className="border-orange-500/50 bg-gradient-to-br from-orange-50 to-orange-100/50 dark:from-orange-900/20 dark:to-orange-900/10">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-orange-500 mt-0.5" />
              <div>
                <h3 className="font-semibold text-orange-900 dark:text-orange-100">
                  {budget.isAtLimit
                    ? "Environment size limit reached"
                    : "Approaching environment size limit"}
                </h3>
                <p className="text-sm text-orange-700 dark:text-orange-200 mt-1">
                  {budget.currentSize.toLocaleString()} /{" "}
                  {budget.hardLimit.toLocaleString()} bytes ({budget.percentage}
                  %)
                  {budget.isAtLimit && " - You cannot add more secrets"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setShowAddModal(true)}
            disabled={activeScope === SecretScope.PROJECT && !selectedProjectId}
            className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Secret
          </Button>

          {activeScope === SecretScope.PROJECT && (
            <Button
              onClick={() => setShowImportFromAccountModal(true)}
              variant="outline"
              disabled={!selectedProjectId}
            >
              <Link className="h-4 w-4 mr-2" />
              Link from Account
            </Button>
          )}

          <Button
            onClick={() => setShowImportEnvModal(true)}
            variant="outline"
            disabled={activeScope === SecretScope.PROJECT && !selectedProjectId}
          >
            <Upload className="h-4 w-4 mr-2" />
            Import .env
          </Button>

          <Button
            onClick={handleToggleGlobalMode}
            variant="outline"
            disabled={activeScope === SecretScope.PROJECT && !selectedProjectId}
          >
            {activeMode === "lite" ? (
              <>
                <Split className="h-4 w-4 mr-2" />
                Split Environments
              </>
            ) : (
              <>
                <Merge className="h-4 w-4 mr-2" />
                Merge to Base
              </>
            )}
          </Button>

          <Button variant="ghost" size="icon">
            <HelpCircle className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search keys..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 w-64"
            />
          </div>

          <Select
            value={typeFilter || "all"}
            onValueChange={(value) => setTypeFilter(value === "all" ? "" : value as SecretType)}
          >
            <SelectTrigger className="w-[140px]">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Type: All" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value={SecretType.VISIBLE}>Visible</SelectItem>
              <SelectItem value={SecretType.MASKED}>Masked</SelectItem>
              <SelectItem value={SecretType.SENSITIVE}>Sensitive</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Secrets Table */}
      <Card className="overflow-hidden">
        <CardContent className="p-6">
          {activeScope === SecretScope.PROJECT && !selectedProjectId ? (
            <div className="text-center py-12 text-muted-foreground">
              <div className="p-4 bg-gradient-to-br from-purple-500/10 to-purple-600/10 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                <FolderOpen className="h-10 w-10 text-purple-600 dark:text-purple-400" />
              </div>
              <p className="text-lg font-medium">
                Select a project above to view its secrets
              </p>
              <p className="text-sm mt-2">
                Choose a project from the dropdown to manage its environment
                variables
              </p>
            </div>
          ) : (
            <SecretsTable
              secrets={secrets}
              mode={activeMode}
              scope={activeScope}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onUnlink={
                activeScope === SecretScope.PROJECT ? handleUnlink : undefined
              }
              loading={loading}
            />
          )}
        </CardContent>
      </Card>

      {/* Hint */}
      <Card className="bg-gradient-to-br from-blue-50 to-blue-100/50 dark:from-blue-900/20 dark:to-blue-900/10 border-blue-200/50 dark:border-blue-800/50">
        <CardContent className="p-4">
          <p className="text-sm text-blue-900 dark:text-blue-100">
            <strong className="text-blue-800 dark:text-blue-200">
              {activeMode === "lite" ? "Lite mode:" : "Power mode:"}
            </strong>{" "}
            {activeMode === "lite"
              ? 'One base value is used across all environments (Dev, Staging, Production). Click "Split Environments" to customize values per environment.'
              : 'Base → Dev → Staging → Production. Same color boxes = inherited values. Different colors = environment has its own value. Click "Merge to Base" to simplify back to single value mode.'}
          </p>
        </CardContent>
      </Card>

      {/* Modals */}
      <AddSecretModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={handleCreate}
        scope={activeScope}
        projectId={
          activeScope === SecretScope.PROJECT
            ? selectedProjectId || undefined
            : undefined
        }
      />

      <EditSecretModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingSecret(null);
        }}
        onSuccess={handleUpdate}
        secret={editingSecret}
      />

      <ImportEnvModal
        isOpen={showImportEnvModal}
        onClose={() => setShowImportEnvModal(false)}
        onSuccess={handleImportEnv}
        scope={activeScope}
        projectId={
          activeScope === SecretScope.PROJECT
            ? selectedProjectId || undefined
            : undefined
        }
      />

      <ImportFromAccountModal
        isOpen={showImportFromAccountModal}
        onClose={() => setShowImportFromAccountModal(false)}
        onSuccess={handleImportFromAccount}
        projectId={selectedProjectId || ""}
      />
    </div>
  );
}
